/**
 * useDocuments — list the user's indexed/uploaded files for the Documents hub.
 *
 * v1 reference: ``ando-frontend/app/pages/DriveHubPage.tsx`` + the unified
 * documents endpoint at ``/documents/unified/list`` (BE:
 * ``ando-agent/src/api/v1/endpoints/documents.py::unified_list``).
 *
 * Polling cadence: 30s. v1 DriveHub polls the underlying tracked-files
 * endpoint at the same cadence for sync-status updates.
 *
 * No explicit capability gate — every authed user can read their own
 * uploaded files. Drive-/Notion-specific connectors are gated separately at
 * the connector level.
 *
 * ``useDocumentConnections`` (May 22, 2026) mirrors ``useCalendarConnections``
 * — fans out ``GET /connectors/drive/connections`` so the Documents hub can
 * mount the shared ``<ConnectionPicker />``. The list is augmented with a
 * synthetic ``"manual"`` source so locally-uploaded files stay
 * filter-toggleable.
 */

import {
  type ConnectionSummary,
  type DocumentCounts,
  type ListDocumentsOptions,
  type WorkspaceDocument,
  listCapabilityConnections,
  listDocumentCounts,
  listDocuments,
} from "@ando/core";
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { useEffect } from "react";

import { useHasCapability } from "@/features/capabilities";
import { useChatUserId } from "@/features/chat";

export function workspaceDocumentsQueryKey(
  userId: string | null,
  options: ListDocumentsOptions,
): readonly unknown[] {
  return [
    "workspace",
    "documents",
    userId,
    options.sourceFilter ?? null,
    options.statusFilter ?? null,
    options.mimeType ?? null,
    options.view ?? null,
    options.period ?? null,
    options.folder ?? null,
    options.q ?? null,
    options.pinned ?? null,
    options.limit ?? null,
  ] as const;
}

export interface UseDocumentsResult {
  readonly documents: readonly WorkspaceDocument[];
  readonly total: number;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly refetch: () => Promise<unknown>;
  /** Fetch the next page — for on-scroll (virtualized) consumers. */
  readonly fetchNextPage: () => void;
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
}

export interface UseDocumentsHookOptions {
  /**
   * When ``true`` (default) the hook EAGERLY walks up to
   * ``MAX_AUTOFETCH_PAGES`` pages so chip counts / group sections reflect the
   * dataset without user scrolling — the behaviour every existing consumer
   * relies on. Set ``false`` for the virtualized Documents hub, which loads
   * pages on demand via ``fetchNextPage`` as the user scrolls (no page cap,
   * no eager loop).
   */
  readonly autoFetchAll?: boolean;
}

const DEFAULT_LIMIT = 20;
// Jun 24, 2026 — server-side filtering refactor. Each smart-view /
// folder / period / search query is now an SQL filter at the BE
// (``view`` / ``folder`` / ``period`` / ``q`` / ``pinned`` query
// params on ``/unified/list``). The FE no longer iterates over a
// global "all documents" set to compute chip counts (that's
// ``useDocumentCounts`` now) — it just renders the filtered page.
// With smaller per-filter result sets, MAX_AUTOFETCH_PAGES can drop
// from 25 to 10 — the user is rarely scrolling 1000 rows in a
// single folder/view. Scales to 100k+ docs because the filter set
// is small relative to the global table.
const MAX_AUTOFETCH_PAGES = 10;

/**
 * Shared infinite-query definition for the documents list. Consumed by BOTH
 * the ``useDocuments`` hook and the ``/workspace/documents`` route loader
 * (``ensureInfiniteQueryData``) so hover/focus intent-preload warms the FIRST
 * page of the EXACT key the hub reads. ``userId`` is threaded in (the loader
 * reads it synchronously from the session) rather than via the ``useChatUserId``
 * hook, so the definition stays render-free. Query CONFIG only — the hook layers
 * ``enabled`` / ``refetchInterval`` / ``maxPages`` on top (runtime concerns the
 * one-shot loader preload doesn't need).
 */
export function documentsInfiniteQueryOptions(
  userId: string | null,
  options: ListDocumentsOptions = {},
) {
  const limit = options.limit ?? DEFAULT_LIMIT;
  return infiniteQueryOptions({
    queryKey: workspaceDocumentsQueryKey(userId, { ...options, limit }),
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      if (!userId) {
        return {
          status: "ok" as const,
          user_id: "",
          total: 0,
          limit,
          offset: pageParam,
          documents: [],
        } satisfies Awaited<ReturnType<typeof listDocuments>>;
      }
      return await listDocuments(userId, {
        ...options,
        limit,
        offset: pageParam,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce(
        (sum, p) => sum + (p.documents?.length ?? 0),
        0,
      );
      // BE returns ``total`` on every page; once we've drained it (or
      // when a page comes back short), stop fetching.
      if (fetched >= lastPage.total) return undefined;
      if ((lastPage.documents?.length ?? 0) < limit) return undefined;
      return fetched;
    },
    // 30s mirrors v1 DriveHub polling cadence.
    staleTime: 30_000,
  });
}

export function useDocuments(
  options: ListDocumentsOptions = {},
  hookOptions: UseDocumentsHookOptions = {},
): UseDocumentsResult {
  const { autoFetchAll = true } = hookOptions;
  const userId = useChatUserId();

  // Jun 11, 2026 — switched from single ``useQuery(limit=100)`` to an
  // infinite query so every indexed Drive / AI / email-attachment row is
  // visible to the smart-view chips, NOT just the most recent 100 by
  // ``created_at``. Root cause of the "Drive 1" misleading badge:
  // with 466 docs (270 email + 113 ai + 44 manual + 34 drive + 5 chat),
  // the top-100 window held 52 ai + 37 email + 10 manual + ONLY 1 drive —
  // the badge was honest about the window, not the dataset.
  const infinite = useInfiniteQuery({
    ...documentsInfiniteQueryOptions(userId, options),
    enabled: userId !== null,
    refetchInterval: 30_000,
    // Eager consumers cap the retained window at MAX_AUTOFETCH_PAGES; the
    // virtualized hub (``autoFetchAll: false``) keeps every fetched page so
    // scroll position stays stable as pages append on demand.
    maxPages: autoFetchAll ? MAX_AUTOFETCH_PAGES : undefined,
  });

  // Eagerly walk every page so the chip counts and source-group sections
  // reflect the FULL dataset. ``useEffect`` (not in-render) to avoid the
  // setState-during-render footgun; TanStack debounces internally so the
  // loop is safe even when multiple effects fire on the same tick.
  // Skipped in on-scroll mode — the virtualizer drives ``fetchNextPage``.
  const pagesFetched = infinite.data?.pages.length ?? 0;
  useEffect(() => {
    if (
      autoFetchAll &&
      infinite.hasNextPage &&
      !infinite.isFetchingNextPage &&
      pagesFetched < MAX_AUTOFETCH_PAGES
    ) {
      void infinite.fetchNextPage();
    }
  }, [
    autoFetchAll,
    infinite.hasNextPage,
    infinite.isFetchingNextPage,
    pagesFetched,
    infinite.fetchNextPage,
    infinite,
  ]);

  const documents = infinite.data?.pages.flatMap((p) => p.documents ?? []) ?? [];
  const total = infinite.data?.pages[0]?.total ?? 0;

  return {
    documents,
    total,
    isLoading: infinite.isLoading,
    isError: infinite.isError,
    error: infinite.error,
    refetch: () => infinite.refetch(),
    fetchNextPage: () => {
      void infinite.fetchNextPage();
    },
    hasNextPage: infinite.hasNextPage,
    isFetchingNextPage: infinite.isFetchingNextPage,
  };
}

// ---------------------------------------------------------------------------
// useDocumentConnections — drive-capability connections for the ConnectionPicker.
// ---------------------------------------------------------------------------

/**
 * Synthetic ``"manual"`` connection id — surfaces locally-uploaded files as
 * a toggleable picker option even though they have no external vendor token.
 * Pattern parallels Google Drive Web 2026's "My Drive / Recent / Trash"
 * pseudo-sources. The id is stable so ``useActiveConnections`` (localStorage)
 * keeps the user's choice across reloads.
 */
export const DOCUMENT_MANUAL_CONNECTION_ID = "__manual__" as const;

/**
 * Document-side connection shape — kept structurally compatible with both
 * ``ConnectionSummary`` (BE) and ``ConnectionPickerOption`` (UI). Carries the
 * raw ``vendor`` slug so the docs filter can map vendor → ``drive_provider``.
 */
export interface DocumentConnection {
  readonly id: string;
  /**
   * Drive vendor slug — ``google_drive`` | ``onedrive`` | ``sharepoint``
   * | ``notion`` | ``manual``. The synthetic ``"manual"`` value is mapped
   * to ``source_type === "manual_upload"`` documents on the filter side.
   */
  readonly vendor: string;
  /** Human-readable account label (e-mail / workspace name / "Caricati"). */
  readonly account_label: string;
  /** ``true`` when the underlying OAuth token needs to be refreshed. */
  readonly needs_reauth: boolean;
}

export interface UseDocumentConnectionsResult {
  readonly connections: readonly DocumentConnection[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  /** ``false`` when the user lacks the ``drive`` capability. */
  readonly hasAccess: boolean;
  readonly refetch: () => Promise<unknown>;
}

const documentConnectionsQueryKey = ["workspace", "documents", "connections"] as const;

/** Pull a "best" display label out of a BE ``ConnectionSummary``. */
function deriveAccountLabel(c: ConnectionSummary): string {
  if (c.connection_key) return c.connection_key;
  if (typeof c.metadata?.account_email === "string") return c.metadata.account_email;
  if (typeof c.metadata?.email === "string") return c.metadata.email;
  if (typeof c.metadata?.workspace_name === "string") return c.metadata.workspace_name;
  if (c.scope) return c.scope;
  return c.provider ?? c.vendor;
}

/**
 * Shared query definition for the drive-capability connections list. Consumed
 * by BOTH the ``useDocumentConnections`` hook and the ``/workspace/documents``
 * route loader (``ensureQueryData``) so the intent-preload warms the SAME cache
 * entry the ConnectionPicker reads. Query CONFIG only — the hook layers
 * ``enabled`` (capability gate) / ``refetchInterval`` on top. The static key
 * carries no args (the connections list is user-scoped server-side).
 */
export function documentConnectionsQueryOptions() {
  return queryOptions({
    queryKey: documentConnectionsQueryKey,
    queryFn: () => listCapabilityConnections("drive"),
    staleTime: 5 * 60_000,
  });
}

/**
 * GET ``/connectors/drive/connections`` — polled every 5 min (parity with
 * ``useCalendarConnections``). The synthetic ``"manual"`` row is appended
 * client-side so the picker always exposes the upload bucket as a toggle.
 */
export function useDocumentConnections(): UseDocumentConnectionsResult {
  const { t } = useLingui();
  // ``drive`` is the canonical capability slug — same one ``driveSpec`` and
  // the integrations index use.
  const hasAccess = useHasCapability("drive");

  const query = useQuery({
    ...documentConnectionsQueryOptions(),
    enabled: hasAccess,
    refetchInterval: 5 * 60_000,
  });

  const rawConnections = query.data ?? [];
  const mapped: DocumentConnection[] = rawConnections.map((c) => ({
    id: c.conn_id,
    // White-label drive rows omit ``vendor``; the public provider rides
    // ``provider`` (google_drive/onedrive/…) which is what the picker routes on.
    vendor: c.provider ?? c.vendor,
    account_label: deriveAccountLabel(c),
    needs_reauth: c.needs_reauth,
  }));

  // Always include the manual-upload synthetic row at the tail — uploads are
  // a first-class source for the Documents hub even when no Drive is wired.
  const connections: DocumentConnection[] = [
    ...mapped,
    {
      id: DOCUMENT_MANUAL_CONNECTION_ID,
      vendor: "manual",
      account_label: t`Caricati manualmente`,
      needs_reauth: false,
    },
  ];

  return {
    connections,
    isLoading: hasAccess ? query.isLoading : false,
    isError: query.isError,
    error: query.error,
    hasAccess,
    refetch: () => query.refetch(),
  };
}


// ---------------------------------------------------------------------------
// useDocumentCounts — per-smart-view badge counts (Jun 24, 2026).
// ---------------------------------------------------------------------------
//
// Single roundtrip to ``GET /documents/counts`` that returns
// ``{ tutti, ricevuti, drive, ando, pinnati }``. The chip strip on the
// Documenti hub reads these counts directly instead of computing them
// from the loaded ``documents`` array — that's the change that lets
// the hub scale past the autofetch window (the counts are accurate even
// when the FE has only the filtered slice loaded).

const DOCUMENT_COUNTS_QUERY_KEY = ["workspace", "documents", "counts"] as const;

export interface UseDocumentCountsResult {
  readonly counts: DocumentCounts;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly refetch: () => Promise<unknown>;
}

const EMPTY_COUNTS: DocumentCounts = {
  tutti: 0,
  ricevuti: 0,
  drive: 0,
  ando: 0,
  pinnati: 0,
};

/**
 * Shared query definition for the per-smart-view badge counts. Consumed by BOTH
 * the ``useDocumentCounts`` hook and the ``/workspace/documents`` route loader
 * (``ensureQueryData``) so the intent-preload warms the SAME cache entry the
 * chip strip reads. ``userId`` is threaded in (the loader reads it synchronously
 * from the session; the hook reads it via ``useChatUserId``) because the counts
 * key is user-scoped — a loader that dropped it would warm a dead cache slice.
 * Query CONFIG only — the hook layers ``enabled`` / ``refetchInterval`` on top.
 */
export function documentCountsQueryOptions(userId: string | null) {
  return queryOptions({
    queryKey: [...DOCUMENT_COUNTS_QUERY_KEY, userId] as const,
    queryFn: () => listDocumentCounts(),
    // Counts shift on upload / pin / delete — pair the staleTime with
    // the list query so chip badge and row count converge in the same
    // refresh tick.
    staleTime: 30_000,
  });
}

export function useDocumentCounts(): UseDocumentCountsResult {
  const userId = useChatUserId();
  const query = useQuery({
    ...documentCountsQueryOptions(userId),
    enabled: userId !== null,
    refetchInterval: 30_000,
  });

  return {
    counts: query.data?.counts ?? EMPTY_COUNTS,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => query.refetch(),
  };
}
